import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { datadogRum } from "@datadog/browser-rum";
import { AuditLogTypes } from "@constants/audit-log-types";
import { AuditLogLevel } from "@enums/audit-log-level";
import { useAuditLogger } from "@hooks/use-audit-logger";
import { AcuantCameraOptions } from "@idw-types/acuant-camera-ui";
import {
    AcuantDocumentProcessingValidationResult,
    AcuantDocumentProcessingResult,
    AcuantDocumentProcessingResultImageData
} from "@idw-types/common/acuant-document-processing-result";
import { CaptureStep } from "@idw-types/data/capture-step";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { getCaptureStepNativeCameraButtonText } from "@utils/capture-step-util";
import { Logger } from "@utils/logger";
import { PrimaryButton } from "../primary-button/primary-button";
import { CustomAcuantCameraUI } from "../custom-acuant-camera-ui/custom-acuant-camera-ui";
import { AcuantCameraToolbarContainer } from "./acuant-camera-toolbar-container/acuant-camera-toolbar-container";
import { AcuantCameraCaptureButtonContainer } from "./acuant-camera-capture-button-container/acuant-camera-capture-button-container";
import styles from "./acuant-document-capture-container.less";

type AcuantDocumentCaptureContainerProps = {
    captureStep: CaptureStep;
    isCapturing: boolean;
    isProcessing: boolean;
    setIsCapturingAndProcessing: (isCapturing: boolean, isProcessing: boolean) => void;
    setAcuantValidationResults: (results: AcuantDocumentProcessingValidationResult) => void;
    setImageUrl: (imageUrl: string) => void;
    setShowCaptureError: (showCaptureError: boolean) => void;
};

export const AcuantDocumentCaptureContainer = (props: AcuantDocumentCaptureContainerProps) => {
    const {
        captureStep,
        isCapturing,
        isProcessing,
        setIsCapturingAndProcessing,
        setAcuantValidationResults,
        setImageUrl,
        setShowCaptureError
    } = props;

    const { writeAuditLog } = useAuditLogger();
    const content = useSelector(getLanguageContent);
    const [liveCaptureMessage, setLiveCaptureMessage] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const options: AcuantCameraOptions = {
        text: {
            NONE: content.acuant.camera.none,
            SMALL_DOCUMENT: content.acuant.camera.smallDocument,
            GOOD_DOCUMENT: null,
            BIG_DOCUMENT: content.acuant.camera.bigDocument,
            CAPTURING: content.acuant.camera.capturing,
            TAP_TO_CAPTURE: content.acuant.camera.tapToCature,
        },
        triggerTime: 3000
    };

    const handleChange = () => {

    };

    const onCaptured = (result: AcuantDocumentProcessingResultImageData) => {
        if (result) {
            Logger.info(`[AcuantCameraDirective] Image Captured`);
            Logger.info(`[AcuantCameraDirective] Image width: ${result.width}, Image height: ${result.height}`);
        }
        writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_CAPTURED);
        setIsCapturingAndProcessing(false, true);
    };

    const onCropped = (captureResult: AcuantDocumentProcessingResult) => {
        Logger.info('[AcuantCameraDirective] Image Cropped');
        if (captureResult) {
            Logger.info(`[AcuantCameraDirective] Acuant validation detected cardType: ${captureResult.cardType}`);
            Logger.info(`[AcuantCameraDirective] Acuant validation detected dpi: ${captureResult.dpi}`);
            Logger.info(`[AcuantCameraDirective] Acuant validation detected glare: ${captureResult.glare}`);
            Logger.info(`[AcuantCameraDirective] Acuant validation detected sharpness: ${captureResult.sharpness}`);
            Logger.info(`[AcuantCameraDirective] Acuant validation detected moire: ${captureResult.moire}`);
            Logger.info(`[AcuantCameraDirective] Acuant validation detected moireraw: ${captureResult.sharpness}`);
            Logger.info('[AcuantCameraDirective] image cropped, send it up');

            setAcuantValidationResults({
                cardType: captureResult.cardType,
                dpi: captureResult.dpi,
                glare: captureResult.glare,
                sharpness: captureResult.sharpness,
                moire: captureResult.moire,
                moireraw: captureResult.sharpness
            });

            setIsCapturingAndProcessing(false, false);
            setImageUrl(captureResult.image.data)
            delete captureResult.image.data;
        } else {
            writeAuditLog(AuditLogTypes.DCUI_ACUANT_IMAGE_CROPPING_ERROR, AuditLogLevel.ERROR);
            Logger.info('[AcuantCameraDirective] Cropping error, restart the capture');
            setShowCaptureError(true);
            setIsCapturingAndProcessing(false, false);
        }
    };

    const onLiveCaptureError = async (error: string, code: number) => {
        writeAuditLog(AuditLogTypes.DCUI_ACUANT_LIVE_CAPTURE_ERROR, AuditLogLevel.ERROR);
        switch (code) {
            case AcuantJavascriptWebSdk.SEQUENCE_BREAK_CODE: // ios 15 bug
                Logger.error('[AcuantCameraDirective] Camera failed due to ios 15 bug. Start manual capture', null, error);
                break;
            case AcuantJavascriptWebSdk.START_FAIL_CODE: // camera not supported or permission not granted
                Logger.error('[AcuantCameraDirective] Live Camera failed to open. Start manual capture.', null, error);
                break;
            case AcuantJavascriptWebSdk.REPEAT_FAIL_CODE: //camera has repeatedly failed, open manual
                Logger.error('[AcuantCameraDirective] Camera has failed to open previously. Start manual capture.', null, error);
                break;
            default:
                // unknown error (shouldn't happen)
                Logger.error('[AcuantCameraDirective] Unknown camera failure. Start manual capture.', null, error);
                break;
        }

        console.error(error);
        Logger.info('[AcuantCameraDirective] There was an error starting the camera: ', error);
        if (document.fullscreenElement) {
            await document.exitFullscreen();
        }
        if (typeof AcuantCameraUI !== "undefined") {
            AcuantCamera.isCameraSupported = false;
        }

        writeAuditLog(AuditLogTypes.DCUI_ACUANT_LIVE_CAMERA_SWITCHED_TO_MANUAL);
        writeAuditLog(AuditLogTypes.DCUI_ACUANT_MOBILE_MANUAL_CAPTURE_SET);

        customCameraUI.end();

        writeAuditLog(AuditLogTypes.DCUI_ACUANT_MANUAL_CAMERA_STARTED);
        setIsCapturingAndProcessing(true, false);
        startManualCamera();
    };

    const onManualCaptureError = (error: string, code: number) => {
        writeAuditLog(AuditLogTypes.DCUI_ACUANT_MANUAL_CAPTURE_ERROR, AuditLogLevel.ERROR);
        Logger.warn('[AcuantCameraDirective] There was an error manually starting the camera', null, error);
        console.error(error, code);
        setIsCapturingAndProcessing(false, false);
    };

    const onLiveCaptureStateChange = (newText: string) => {
        setLiveCaptureMessage(newText);
    };

    const customCameraUI = CustomAcuantCameraUI({
        captureStepType: captureStep.type,
        captureCallback: { onCaptured, onCropped },
        onLiveCaptureError: onLiveCaptureError,
        onLiveCaptureStateChange: onLiveCaptureStateChange,
        options: options
    });

    const onClickCloseCamera = async () => {
        customCameraUI.end();
        if (document.fullscreenElement) {
            await document.exitFullscreen();
        }
        setIsCapturingAndProcessing(false, false);
    };

    const onClickCameraCaptureButton = () => {
        customCameraUI.capture({ onCaptured, onCropped }, "TAP");
    };

    const onClickSwitchToNativeCamera = async () => {
        customCameraUI.end();
        if (document.fullscreenElement) {
            await document.exitFullscreen();
        }
        if (typeof AcuantCameraUI !== "undefined") {
            AcuantCamera.isCameraSupported = false;
        }
        writeAuditLog(AuditLogTypes.DCUI_ACUANT_LIVE_CAMERA_SWITCHED_TO_MANUAL);
        writeAuditLog(AuditLogTypes.DCUI_ACUANT_MOBILE_MANUAL_CAPTURE_SET);
        writeAuditLog(AuditLogTypes.DCUI_ACUANT_MANUAL_CAMERA_STARTED);
        setIsCapturingAndProcessing(true, false);
        startManualCamera();
    };

    const startAcuantCamera = () => {
        Logger.info('[AcuantCameraDirective] Start Acuant WebRTC Camera');
        try {
            customCameraUI.start();
        } catch (error) {
            writeAuditLog(AuditLogTypes.DCUI_ACUANT_LIVE_CAPTURE_ERROR, AuditLogLevel.ERROR);
            Logger.error(`[AcuantCameraDirective] Live capture error`, error);
            const dataDogerror = new Error(`[AcuantCameraDirective] Live capture error`);
            datadogRum.addError(dataDogerror, { error });
        }
    };

    const startManualCamera = () => {
        Logger.info(`[AcuantCameraDirective] Start Acuant Manual Capture`);
        try {
            AcuantCamera.startManualCapture({ onCaptured, onCropped, onError: onManualCaptureError });
        } catch (error) {
            writeAuditLog(AuditLogTypes.DCUI_ACUANT_MANUAL_CAPTURE_ERROR, AuditLogLevel.ERROR);
            Logger.error(`[AcuantCameraDirective] Manual capture error`, error);
            const dataDogerror = new Error(`[AcuantCameraDirective] Manual capture error`);
            datadogRum.addError(dataDogerror, { error: error });
        }
    };

    const onClickCapture = () => {
        if (inputRef.current) {
            inputRef.current.value = null;
        }
        setIsCapturingAndProcessing(true, false);
        writeAuditLog(AuditLogTypes.DCUI_CAPTURE_BUTTON_HIT);
        if (typeof AcuantCameraUI !== "undefined") {
            if (AcuantCamera.isCameraSupported) {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_LIVE_CAMERA_LAUNCHED);
                startAcuantCamera();
            } else {
                writeAuditLog(AuditLogTypes.DCUI_ACUANT_MANUAL_CAMERA_STARTED);
                startManualCamera();
            }
        } else {
            Logger.info('[AcuantCameraDirective] No Acuant Camera available!');
        }
    };

    const buttonText = useMemo(() => {
        return getCaptureStepNativeCameraButtonText(captureStep.type, content);
    }, [captureStep.type, content]);

    const cameraContainerClassName = useMemo(() => {
        return isCapturing && typeof AcuantCameraUI !== "undefined" && AcuantCamera.isCameraSupported ? "" : styles.hideCamera;
    }, [isCapturing, AcuantCameraUI, AcuantCamera.isCameraSupported]);

    return (
        <div className={styles.acuantDocumentCaptureContainer}>
            <input
                ref={inputRef}
                className={styles.fileInput}
                type="file"
                accept="image/*"
                onChange={handleChange}
                id={"capture-input"}
            />
            <div id="acuant-camera-container" className={classNames(styles.acuantCameraContainer, cameraContainerClassName)}>
                <AcuantCameraToolbarContainer
                    onClickCloseCamera={onClickCloseCamera}
                    onClickSwitchToNativeCamera={onClickSwitchToNativeCamera}
                />
                <div id="acuant-camera" />
                {liveCaptureMessage && (
                    <div className={styles.liveCaptureMessage}>
                        <span>{liveCaptureMessage}</span>
                    </div>
                )}
                <AcuantCameraCaptureButtonContainer onClickCaptureButton={onClickCameraCaptureButton} />
            </div>
            {!isCapturing && !isProcessing && (
                <div className={styles.buttonContainer}>
                    <PrimaryButton
                        disabled={false}
                        label={buttonText}
                        type={"button"}
                        automationId={"start-capture"}
                        id={"start-capture"}
                        hideSpinner={true}
                        onClick={onClickCapture}
                    />
                </div>
            )}
        </div>
    );
};